/**
 * PHSIMS Web Admin Frontend - Material-UI Theme
 * Author: Iswe Solutions Team
 *
 * Zambia National Public Health Institute (ZNPHI) Branding
 * Colors derived from Zambian national identity — forest green, copper, ember.
 *
 * ALL semantic colors are defined in `theme/colors.ts`.
 * This file only configures MUI's palette & component overrides.
 */

import { defaultTheme } from 'react-admin';
import { createTheme } from '@mui/material/styles';
import { ZAMBIA, SEMANTIC, SURFACE, TEXT } from './theme/colors';

/**
 * Custom Material-UI theme with Zambia colors
 * Based on official Zambian flag colors, matured into a professional palette.
 */
export const zambiaTheme = createTheme({
  ...defaultTheme,

  palette: {
    // Primary: Zambian Forest Green
    primary: {
      main: ZAMBIA.forest,
      light: ZAMBIA.forestLight,
      dark: ZAMBIA.forestDark,
      contrastText: '#ffffff',
    },

    // Secondary: Zambian Copper (matured orange)
    secondary: {
      main: ZAMBIA.copper,
      light: ZAMBIA.copperLight,
      dark: ZAMBIA.copperDark,
      contrastText: '#ffffff',
    },

    // Warning: Deep amber (not neon orange)
    warning: {
      main: SEMANTIC.attention,
      light: ZAMBIA.copperLight,
      dark: ZAMBIA.copperDark,
      contrastText: '#ffffff',
    },

    // Error: Ember red (warm, not alarm-bell)
    error: {
      main: SEMANTIC.danger,
      light: ZAMBIA.emberLight,
      dark: ZAMBIA.emberDark,
    },

    // Success: Forest green (cohesive with brand)
    success: {
      main: SEMANTIC.positive,
      light: ZAMBIA.forestLight,
      dark: ZAMBIA.forestDark,
    },

    // Info: Deep indigo (not generic blue)
    info: {
      main: SEMANTIC.insight,
      light: '#6366f1',
      dark: '#312e81',
    },

    // Background
    background: {
      default: SURFACE.page,
      paper: SURFACE.card,
    },

    // Text
    text: {
      primary: TEXT.primary,
      secondary: TEXT.secondary,
    },
  },
  
  typography: {
    fontFamily: [
      'system-ui',
      '-apple-system',
      'Segoe UI',
      'Roboto',
      'Ubuntu',
      'Helvetica Neue',
      'Arial',
      'sans-serif',
    ].join(','),
    
    h1: {
      fontWeight: 700,
      fontSize: '2.5rem',
      color: TEXT.primary,
    },

    h2: {
      fontWeight: 600,
      fontSize: '2rem',
      color: TEXT.primary,
    },

    h3: {
      fontWeight: 600,
      fontSize: '1.5rem',
      color: TEXT.primary,
    },

    h4: {
      fontWeight: 600,
      fontSize: '1.25rem',
      color: TEXT.primary,
    },
    
    button: {
      fontWeight: 600,
      textTransform: 'none',  // Preserve case
    },
  },
  
  components: {
    // Global baseline: prevent horizontal scroll on all pages
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          overflowX: 'hidden',
        },
        body: {
          overflowX: 'hidden',
        },
        // Contain Leaflet's z-indexes. Leaflet assigns its own panes and
        // controls values up to 1000, and .leaflet-container is already
        // position: relative -- so with no z-index of its own it never forms a
        // stacking context, and those values compete with the whole page. Maps
        // then paint over sticky headers and side panels, which is what they
        // were doing on the SitRep detail page.
        //
        // z-index: 0 makes the container a stacking context, so everything
        // Leaflet does stays inside the map. Nothing about the map's own
        // appearance or behaviour changes.
        '.leaflet-container': {
          zIndex: 0,
        },
      },
    },

    // AppBar (Header)
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: `linear-gradient(135deg, ${ZAMBIA.forest} 0%, ${ZAMBIA.forestDark} 100%)`,
          boxShadow: '0 2px 8px rgba(13, 92, 46, 0.2)',
        },
      },
    },
    
    // Buttons
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '8px 20px',
          fontWeight: 600,
        },
        contained: {
          boxShadow: '0 2px 8px rgba(13, 92, 46, 0.3)',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(13, 92, 46, 0.4)',
          },
        },
      },
    },
    
    // Cards
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
          },
        },
      },
    },
    
    // Paper
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    
    // Tabs: always scrollable so tabs never overflow off-screen
    MuiTabs: {
      defaultProps: {
        variant: 'scrollable',
        scrollButtons: 'auto',
        allowScrollButtonsMobile: true,
      },
      styleOverrides: {
        root: {
          // Prevent tabs from pushing parent wider in flex layouts
          maxWidth: '100%',
          minWidth: 0,
          width: '100%',
          boxSizing: 'border-box',
        },
      },
    },

    // Table
    MuiTableContainer: {
      styleOverrides: {
        root: {
          // Allow wide tables to scroll horizontally within the page
          overflowX: 'auto',
          maxWidth: '100%',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: SURFACE.muted,
          '& .MuiTableCell-head': {
            fontWeight: 700,
            color: ZAMBIA.forest,
            textTransform: 'uppercase',
            fontSize: '0.75rem',
            letterSpacing: '0.5px',
          },
        },
      },
    },
    
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: SURFACE.muted,
          },
        },
      },
    },

    // Push snackbars above the fixed 64px footer
    MuiSnackbar: {
      styleOverrides: {
        anchorOriginBottomCenter: { bottom: '80px !important' },
        anchorOriginBottomLeft: { bottom: '80px !important' },
        anchorOriginBottomRight: { bottom: '80px !important' },
      },
    },
  },
});

export default zambiaTheme;
